import { useMemo } from 'react';
import { DatabaseBackup } from 'lucide-react';
import { BackupConsole, type BackupConsoleTransport } from '@speles7172/backup-console';
import '@speles7172/backup-console/styles.css';
import * as backupsApi from '../api/backups';
import { useInTabbedPage } from '../components/common/TabbedPage';

/**
 * Backup & Recovery.
 *
 * The whole screen is `<BackupConsole>` from `@speles7172/backup-console` — the
 * schedule grid, the run history and the restore list all come from the package,
 * so every project on it gets the same screen rather than a per-repo
 * reimplementation of one.
 *
 * The same deliberate exception to the component rules that `AuditLogsPage`
 * takes, and recorded in CLAUDE.md for the same reason: those rules exist so
 * screens built *here* stay consistent, and this screen is not built here.
 *
 * `startRestore` hands the console the run's **row id**, not its snapshot id.
 * The endpoint reads the snapshot id and the store from that row, because one
 * snapshot id can belong to both a database and a files run — the runner
 * refuses to guess between them, and this is where that is answered so the
 * screen never has to know.
 */
export function BackupsPage() {
  const transport = useMemo<BackupConsoleTransport>(
    () => ({
      listRuns: (query) => backupsApi.listRuns(query),
      listRestores: (query) => backupsApi.listRestores(query),
      getSchedule: () => backupsApi.getSchedule(),
      saveSchedule: (schedule) => backupsApi.saveSchedule(schedule),
      startBackup: (store) => backupsApi.startBackup(store),
      startRestore: (id) => backupsApi.startRestore(id),
    }),
    // Built once: the console takes the transport as an identity and reloads
    // when it changes.
    [],
  );

  const embedded = useInTabbedPage();

  return (
    <div className="space-y-6">
      {!embedded && (
        <div className="flex items-center gap-3">
          <DatabaseBackup size={24} />
          <h1 className="text-2xl font-bold">Backup &amp; Recovery</h1>
        </div>
      )}

      {/* `title={null}` exactly as `AuditLogsPage` passes it: the heading above
          is the page's, and a second one from the console would read as a
          mistake. */}
      <BackupConsole transport={transport} title={null} />
    </div>
  );
}
